import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import './ParkingRates.css';

const ParkingRatesSection = styled.section`
  padding: 4rem 0;
  background: white;

  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  text-align: center;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AccentLine = styled(motion.div)`
  width: 60px;
  height: 3px;
  background: #FF0707;
  margin: 0 auto 3rem;
`;

const RatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const RateCard = styled(motion.div)`
  background: white;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 3rem;
  text-align: center;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 280px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: 200px;
  }
`;

const Price = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #C31321;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0.25rem;
  
  .currency {
    font-size: 1.25rem;
    font-weight: 600;
    align-self: flex-start;
    margin-top: 0.25rem;
  }
  
  .number {
    font-size: 3.5rem;
    font-weight: 700;
  }
  
  .unit {
    font-size: 1.25rem;
    font-weight: 600;
    align-self: flex-end;
    margin-bottom: 0.25rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    
    .currency {
      font-size: 1rem;
    }
    
    .number {
      font-size: 2.5rem;
    }
    
    .unit {
      font-size: 1rem;
    }
  }
`;

const Type = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Class = styled.div`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 1.5rem;
`;

const Locations = styled.div`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 0.75rem;
`;

const StartingPrice = styled.div`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 1.5rem;
`;

const LearnMoreButton = styled.button`
  background: #FF0707;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;

  &:hover {
    background: #d60000;
    transform: translateY(-1px);
  }
`;

const ContactBanner = styled.div`
  background: #7F1B1C;
  padding: 2rem;
  margin-top: 3rem;
  margin-left: calc(-20px - 2vw);
  margin-right: calc(-20px - 2vw);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20rem;
  text-align: center;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
    padding: 1.5rem;
  }
`;

const BannerText = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ContactButton = styled.button`
  background: linear-gradient(135deg, #2d1b1b 0%, #FF0707 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 7, 7, 0.3);
  }
`;

const ParkingRates = () => {
  const rates = [
    {
      price: "N$ 8 / hour",
      type: "On-Street",
      class: "CLASS A",
      buttonText: "Learn More"
    },
    {
      price: "N$ 5 / hour",
      type: "On-Street",
      class: "CLASS B",
      buttonText: "Learn More"
    },
    {
      price: "N$ 3 / hour",
      type: "On-Street",
      class: "CLASS C",
      buttonText: "Learn More"
    },
    {
      type: "Off-Street",
      locations: "3 Locations",
      startingPrice: "Starting From N$ 2",
      buttonText: "Learn More"
    }
  ];

  return (
    <ParkingRatesSection>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Parking Options and Rates
        </SectionTitle>
        
        <AccentLine
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />

        <RatesGrid
          as={motion.div}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {rates.map((rate, index) => (
            <RateCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
            >
              {rate.price && (
                <Price>
                  <span className="currency">N$</span>
                  <span className="number">{rate.price.split(' ')[1]}</span>
                  <span className="unit">/ hour</span>
                </Price>
              )}
              <Type>{rate.type}</Type>
              {rate.class && <Class>{rate.class}</Class>}
              {rate.locations && <Locations>{rate.locations}</Locations>}
              {rate.startingPrice && <StartingPrice>{rate.startingPrice}</StartingPrice>}
              <LearnMoreButton>{rate.buttonText}</LearnMoreButton>
            </RateCard>
          ))}
        </RatesGrid>

        <ContactBanner
          as={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <BannerText>
            Interested in a monthly off-street parking contract
          </BannerText>
          <ContactButton>
            Contact us
          </ContactButton>
        </ContactBanner>
      </Container>
    </ParkingRatesSection>
  );
};

export default ParkingRates; 